import { Component } from '@angular/core';
import {
  ProductClassification, ProductType, ProductSpecification, ThemeReplacementTag
} from 'situview-webapi-client';
import { AuthService, BreadcrumbService, StateService, ProductService } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { Guid } from 'guid-typescript';
import { Observable } from 'rxjs';
import { FormControl } from '@angular/forms';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'product-categorization',
  templateUrl: './product-categorization.component.html',
  styleUrls: ['./product-categorization.component.less']
})

export class ProductCategorization extends SituBaseComponent {

  productClassifications?: ProductClassification[];
  productTypes?: ProductType[];
  productSpecifications?: ProductSpecification[];
  themeReplacementTags?: ThemeReplacementTag[];

  selectedProductClassificationId?: string;
  selectedProductTypeId?: string;

  selectedProductClassification?: ProductClassification;
  selectedProductType?: ProductType;

  classificationControl = new FormControl();
  filteredClassifications: Observable<ProductClassification[]> | undefined;

  typeControl = new FormControl();
  filteredTypes: Observable<ProductType[]> | undefined;

  addProductClassificationMode: boolean = false;
  updateProductClassificationMode: boolean = false;

  addProductTypeMode: boolean = false;
  updateProductTypeMode: boolean = false;

  private _loading = 0;
  // The page is only hidden for the first load, so later reloads keep selections, open panels and scroll position
  hasLoaded = false;
  public get loading(): number { return this._loading; }

  public set loading(val: number) {
    this._loading = val;
    if (val == 0) {
      this.hasLoaded = true;
    }
    this.breadcrumbService.setIsLoading(val != 0);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private productService: ProductService) {
    super(authService, stateService);

    this.getProductClassifications();
    this.getProductTypes();
    this.getProductSpecifications();
    this.getThemeReplacementTags();
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.filteredClassifications = this.classificationControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterClassifications(value))
    );

    this.filteredTypes = this.typeControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterTypes(value))
    );
  }

  displayClassificationName(productClassification: ProductClassification | undefined): string {
    if (!productClassification) {
      return '';
    }

    // The blank classification groups the product types that have no classification
    return productClassification.name || 'Unclassified';
  }

  displayTypeName(productType: ProductType | undefined): string {
    return productType?.name || '';
  }

  onClassificationSelected(productClassification: ProductClassification | undefined) {
    this.selectedProductClassificationId = productClassification?.id;
    this.selectedProductClassification = productClassification ? new ProductClassification(productClassification) : undefined;

    this.clearTypeSelection();
  }

  onTypeSelected(productType: ProductType | undefined) {
    this.selectedProductTypeId = productType?.id;
    this.selectedProductType = productType ? new ProductType(productType) : undefined;
  }

  clearClassificationSelection() {
    this.classificationControl.setValue('');
    this.onClassificationSelected(undefined);
  }

  clearTypeSelection() {
    this.typeControl.setValue('');
    this.onTypeSelected(undefined);
  }

  updateClassificationMode() {
    this.updateProductClassificationMode = true;
    this.classificationControl.disable();
  }

  addClassificationMode() {
    this.addProductClassificationMode = true;
    this.classificationControl.disable();

    this.selectedProductClassification = new ProductClassification();
    this.selectedProductClassification.id = Guid.create().toString();
    this.selectedProductClassification.name = "New Product Classification";
  }

  cancelAddOrUpdateClassificationMode() {
    var wasAdding = this.addProductClassificationMode;

    this.updateProductClassificationMode = false;
    this.addProductClassificationMode = false;
    this.classificationControl.enable();

    if (wasAdding) {
      this.clearClassificationSelection();
    } else {
      // Discard the unsaved edits but keep the selected product type
      this.selectedProductClassification = new ProductClassification(this.classificationControl.value);
    }
  }

  addClassification() {
    this.productService.AddProductClassification(this.selectedProductClassification!)
    .then((productClassification: ProductClassification | undefined) => {
      this.getProductClassifications();

      if (!productClassification) {
        this.snackBar.open("Failed to add Product Classification.");
        return;
      }

      this.addProductClassificationMode = false;
      this.classificationControl.enable();
      this.classificationControl.setValue(productClassification);
      this.onClassificationSelected(productClassification);
    });
  }

  updateClassification() {
    this.productService.UpdateProductClassification(this.selectedProductClassification!)
    .then((productClassification: ProductClassification | undefined) => {
      this.getProductClassifications();

      if (!productClassification) {
        this.snackBar.open("Failed to update Product Classification.");
        return;
      }

      this.updateProductClassificationMode = false;
      this.classificationControl.enable();
      this.classificationControl.setValue(productClassification);
      this.selectedProductClassificationId = productClassification.id;
      this.selectedProductClassification = new ProductClassification(productClassification);
    });
  }

  updateTypeMode() {
    this.updateProductTypeMode = true;
    this.typeControl.disable();
  }

  addTypeMode() {
    this.addProductTypeMode = true;
    this.typeControl.disable();

    this.selectedProductType = new ProductType();
    this.selectedProductType.id = Guid.create().toString();
    this.selectedProductType.name = "New Product Type";
    this.selectedProductType.templateName = "material | product | productidentifier";
    this.selectedProductType.productClassificationId = this.selectedProductClassificationId;
  }

  cancelAddOrUpdateTypeMode() {
    var wasAdding = this.addProductTypeMode;

    this.updateProductTypeMode = false;
    this.addProductTypeMode = false;
    this.typeControl.enable();

    if (wasAdding) {
      this.clearTypeSelection();
    } else {
      this.onTypeSelected(this.typeControl.value);
    }
  }

  addType() {
    this.productService.AddProductType(this.selectedProductType!)
    .then((productType: ProductType | undefined) => {
      this.getProductTypes();

      if (!productType) {
        this.snackBar.open("Failed to add Product Type.");
        return;
      }

      this.addProductTypeMode = false;
      this.typeControl.enable();
      this.typeControl.setValue(productType);
      this.onTypeSelected(productType);
    });
  }

  updateType() {
    this.productService.UpdateProductType(this.selectedProductType!)
    .then((productType: ProductType | undefined) => {
      this.getProductTypes();

      if (!productType) {
        this.snackBar.open("Failed to update Product Type.");
        return;
      }

      this.updateProductTypeMode = false;
      this.typeControl.enable();
      this.typeControl.setValue(productType);
      this.onTypeSelected(productType);
    });
  }

  addSpecification(productSpecification: ProductSpecification) {
    this.productService.AddProductSpecification(productSpecification)
    .then((productSpecification: ProductSpecification | undefined) => {
      // Only reload on success: reloading rebuilds the editor and would discard the unsaved specification
      if (!productSpecification) {
        this.snackBar.open("Failed to add Product Specification.");
        return;
      }

      this.getProductSpecifications();
      this.onSpecificationEditingChange(false);
    });
  }

  updateSpecification(productSpecification: ProductSpecification) {
    this.productService.UpdateProductSpecification(productSpecification)
    .then((productSpecification: ProductSpecification | undefined) => {
      if (!productSpecification) {
        this.snackBar.open("Failed to update Product Specification.");
        return;
      }

      this.getProductSpecifications();
      this.onSpecificationEditingChange(false);
    });
  }

  // Changing the classification or product type would rebuild the specifications and lose the open one
  onSpecificationEditingChange(editing: boolean) {
    if (editing) {
      this.classificationControl.disable();
      this.typeControl.disable();
    } else {
      if (!this.addProductClassificationMode && !this.updateProductClassificationMode) {
        this.classificationControl.enable();
      }
      if (!this.addProductTypeMode && !this.updateProductTypeMode) {
        this.typeControl.enable();
      }
    }
  }

  getProductClassifications() {
    this.loading++;
    this.productService.LoadProductClassifications()
    .then((productClassifications) => {
      if (productClassifications.length == 0) {
        this.snackBar.open("Failed to load Product Classifications.");
      }

      // Add a blank classification, for product types that are not classified
      var blankClassification = new ProductClassification({ id: undefined, name: '' });
      this.productClassifications = [blankClassification, ...productClassifications];

      this.loading--;
    }).catch((e) => {
      this.loading--;
      this.snackBar.open("Failed to load Product Classifications.");
    });
  }

  getProductTypes() {
    this.loading++;
    this.productService.LoadProductTypes()
    .then((productTypes) => {
      if (productTypes.length == 0) {
        this.snackBar.open("Failed to load Product Types.");
      }

      this.productTypes = productTypes;

      this.loading--;
    }).catch((e) => {
      this.loading--;
      this.snackBar.open("Failed to load Product Types.");
    });
  }

  getProductSpecifications() {
    this.loading++;
    this.productService.LoadProductSpecifications()
    .then((productSpecifications) => {
      if (productSpecifications.length == 0) {
        this.snackBar.open("Failed to load Product Specifications.");
      }

      this.productSpecifications = productSpecifications;

      this.loading--;
    }).catch((e) => {
      this.loading--;
      this.snackBar.open("Failed to load Product Specifications.");
    });
  }

  getThemeReplacementTags() {
    this.loading++;
    this.productService.LoadThemeReplacementTags(true, false)
    .then((themeReplacementTags) => {
      if (themeReplacementTags.length == 0) {
        this.snackBar.open("Failed to load Replacement Tags.");
      }

      this.themeReplacementTags = themeReplacementTags;

      this.loading--;
    }).catch((e) => {
      this.loading--;
      this.snackBar.open("Failed to load Replacement Tags.");
    });
  }

  filterRelatedReplacementTags(): ThemeReplacementTag[] {
    if (this.themeReplacementTags) {
      return this.themeReplacementTags.filter(e => e.productTypeIds?.includes(this.selectedProductTypeId!))
    } else {
      return [];
    }
  }

  filterNotRelatedReplacementTags(): ThemeReplacementTag[] {
    if (this.themeReplacementTags) {
      return this.themeReplacementTags.filter(e => !e.productTypeIds?.includes(this.selectedProductTypeId!))
    } else {
      return [];
    }
  }

  addRelatedReplacementTag(item: ThemeReplacementTag){
    if(this.selectedProductTypeId && item?.id) {
      item.productTypeIds!.push(this.selectedProductTypeId);
    }
  }

  removeRelatedReplacementTag(item: ThemeReplacementTag){
    if(this.selectedProductTypeId && item?.id) {
      item.productTypeIds = item.productTypeIds!.filter(e => e !== this.selectedProductTypeId)
    }
  }

  updateRelatedReplacementTags() {
    var relatedReplacementTagIds = this.filterRelatedReplacementTags().map(e => e.id!);
    this.productService.UpdateProductTypeRelatedTags(this.selectedProductTypeId!, relatedReplacementTagIds)
    .then((success) => {
      this.getThemeReplacementTags();

      if (!success) {
        this.snackBar.open("Failed to update Product Type Related Tags.");
        return;
      }
    });
  }

  private filterClassifications(filter: any = undefined): ProductClassification[] {
    if (filter instanceof ProductClassification) {
      return [filter];
    }

    var productClassifications = this.productClassifications ?? [];

    // Filter by Search Terms
    if (typeof filter === 'string' || filter instanceof String) {
      var searchTerm = filter.toLowerCase();
      productClassifications = productClassifications.filter(x => this.displayClassificationName(x).toLowerCase().includes(searchTerm));
    }

    return productClassifications;
  }

  private filterTypes(filter: any = undefined): ProductType[] {
    if (filter instanceof ProductType) {
      return [filter];
    }

    // With no classification selected every product type is listed
    var productTypes = (this.productTypes ?? [])
      .filter(item => !this.selectedProductClassification || item.productClassificationId == this.selectedProductClassificationId)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    // Filter by Search Terms
    if (typeof filter === 'string' || filter instanceof String) {
      var searchTerm = filter.toLowerCase();
      productTypes = productTypes.filter(x => x.name).filter(x => x.name!.toLowerCase().includes(searchTerm));
    }

    return productTypes;
  }
}